import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  checkInFromQr,
  fetchMe,
  getLoyaltyStatus,
  identifyGuest,
  joinSession,
  resolveQr,
  type JoinCodeRequiredDetails,
} from '../lib/api.js'
import { useSessionStore } from '../stores/session.store.js'
import { NintaiGOHeader } from '../components/NintaiGOHeader.js'
import { useBranchName } from '../lib/useBranchName.js'
import { useStadiumMode, useSeatStore, type SeatLocation } from '../lib/stadium.js'
import { StadiumSeatForm } from '../components/StadiumSeatForm.js'
import { useTenantBranding } from '../lib/useTenantBranding.js'

/**
 * Check-in OhSir con sesiones privadas por mesa.
 *
 * Flujo:
 *  1. El comensal escanea el QR → intentamos ABRIR la sesión (`from-qr`).
 *  2a. Si la mesa estaba libre → la abre y recibe un CÓDIGO. Se lo mostramos
 *      en grande ("tu código es XXXX, compártelo") y un botón "Ir al menú".
 *  2b. Si la mesa YA tenía una sesión activa → el backend responde
 *      `JOIN_CODE_REQUIRED`. Mostramos el formulario "ingresa el código".
 *  3. Al enviar el código → `join`. Correcto → entra a la MISMA sesión y va
 *     al menú. Incorrecto → error en línea, sin entrar.
 *
 * Paso de PRE-REGISTRO (opcional, tras abrir la sesión y ANTES del código):
 *  invitamos al comensal a dejar nombre + teléfono + correo — el pretexto es
 *  avisarle cuando su pedido esté listo (y ubicarlo por nombre). Alimenta el
 *  CRM. Nunca bloquea: "omitir" salta directo al código/menú. Si el negocio
 *  tiene lealtad activa, el copy además ofrece acumular puntos.
 */
type Phase = 'opening' | 'register' | 'opened' | 'need-code' | 'joining' | 'error'

export function CheckIn() {
  const { slug, qrCode } = useParams<{ slug: string; qrCode?: string }>()
  const navigate = useNavigate()
  const setSession = useSessionStore((s) => s.setSession)
  const setLoyalty = useSessionStore((s) => s.setLoyalty)
  const setLoyaltyProgram = useSessionStore((s) => s.setLoyaltyProgram)
  // Sucursal de la sesión: con 18 sucursales el comensal DEBE saber desde el
  // primer momento dónde está ordenando/recogiendo (reporte MilCaguamas). El
  // branchId llega con el check-in (from-qr) → header + copy de bienvenida.
  const sessionBranchId = useSessionStore((s) => s.session?.branchId ?? null)
  const branchName = useBranchName(sessionBranchId)
  // Modo estadio (por negocio): en vez del pre-registro pedimos Sección/Fila/
  // Asiento. Sin el modo, nada de esto se usa.
  const stadium = useStadiumMode()
  const stadiumRef = useRef(stadium)
  stadiumRef.current = stadium
  const seatForSlug = useSeatStore((s) => (slug ? s.bySlug[slug] ?? null : null))
  const setSeat = useSeatStore((s) => s.setSeat)
  const branding = useTenantBranding(slug ?? null)

  const [phase, setPhase] = useState<Phase>('opening')
  const [error, setError] = useState<string | null>(null)
  const [mesaNoDisponible, setMesaNoDisponible] = useState<string | null>(null)
  const [tableCode, setTableCode] = useState<string | null>(null)
  const [openCode, setOpenCode] = useState<string | null>(null)
  const [codeInput, setCodeInput] = useState('')
  const [codeError, setCodeError] = useState<string | null>(null)
  const [retryNonce, setRetryNonce] = useState(0)
  /** OhSir: la sesión es de un pedido PARA LLEVAR (QR de takeout). */
  const [isTakeout, setIsTakeout] = useState(false)

  // ── Pre-registro (paso opcional) ──────────────────────────────────────────
  const [regName, setRegName] = useState('')
  const [regPhone, setRegPhone] = useState('')
  const [regEmail, setRegEmail] = useState('')
  const [regBusy, setRegBusy] = useState(false)
  const [regError, setRegError] = useState<string | null>(null)
  /** Programa de lealtad del negocio (gatea el gancho de "acumula puntos"). */
  const [loyaltyOn, setLoyaltyOn] = useState(false)
  const [loyaltyPpv, setLoyaltyPpv] = useState(0)

  // ── Paso 1: intentar abrir la sesión al montar / reintentar ───────────────
  useEffect(() => {
    if (!qrCode) {
      setPhase('error')
      return
    }
    let alive = true
    setPhase('opening')
    setError(null)
    setMesaNoDisponible(null)
    void (async () => {
      // 1a. ¿Ya tenemos una sesión válida para ESTA mesa? (recarga / volver al
      //     QR). Confirmamos con el QR resuelto + el token contra `me`; si todo
      //     cuadra, este comensal ya pertenece a la mesa → directo al menú,
      //     sin pedirle el código de nuevo.
      const persisted = useSessionStore.getState().session
      if (persisted && !useSessionStore.getState().isExpired()) {
        const qr = await resolveQr(qrCode)
        if (!alive) return
        if (qr.ok && qr.data.tableId === persisted.tableId) {
          const me = await fetchMe(persisted.token)
          if (!alive) return
          if (me.ok) {
            // Modo estadio: si aún no sabemos el asiento, lo pedimos antes del
            // menú (el check-in puede re-montarse al abrir el tiempo real y caer
            // aquí con la sesión recién creada). Fuera del modo: igual que siempre.
            if (stadiumRef.current && qr.data.kind === 'TAKEOUT' && slug && !useSeatStore.getState().bySlug[slug]) {
              setIsTakeout(true)
              setPhase('register')
              return
            }
            navigate(`/r/${slug}/menu`, { replace: true })
            return
          }
        }
      }

      // 1b. Comensal nuevo en esta mesa: intentar ABRIR la sesión.
      const body: { qrCode: string; slug?: string } = { qrCode }
      if (slug) body.slug = slug
      const r = await checkInFromQr(body)
      if (!alive) return

      if (r.ok) {
        setSession(r.data)
        // OhSir takeout: el QR para llevar abre un pedido independiente — no se
        // comparte código. El pago se hará al cerrar el pedido, antes de cocina.
        if (r.data.orderMode === 'TAKEOUT') {
          setIsTakeout(true)
          setTableCode(null)
        } else {
          // QR de mesa: es el primer comensal. Guardamos su código para compartir.
          setOpenCode(r.data.joinCode)
          setTableCode(r.data.tableCode)
        }
        // Estado del programa de lealtad del negocio (best-effort) → decide si el
        // pre-registro además ofrece "acumula puntos". Lo cacheamos en el store
        // para que la tarjeta del menú no lo vuelva a consultar.
        const ls = await getLoyaltyStatus(r.data.tenantId)
        if (!alive) return
        if (ls.ok) {
          setLoyaltyOn(ls.data.enabled)
          setLoyaltyPpv(ls.data.pointsPerVisit)
          setLoyaltyProgram(ls.data)
        } else {
          setLoyaltyProgram({ enabled: false, pointsPerVisit: 0 })
        }
        // Paso de pre-registro ANTES del código/menú (opcional, con "omitir").
        setPhase('register')
        return
      }

      // La mesa ya tiene sesión activa → pedir el código de unión.
      if (r.error.code === 'JOIN_CODE_REQUIRED') {
        const d = r.error.details as JoinCodeRequiredDetails | undefined
        setTableCode(d?.tableCode ?? null)
        setPhase('need-code')
        return
      }

      // Se guarda el mensaje limpio aparte: los estados accionables por el
      // comensal (mesa en limpieza) no deben salir con envoltura tecnica.
      setMesaNoDisponible(r.error.code === 'INVALID_STATE' ? r.error.message : null)
      setError(`${r.error.code}: ${r.error.message}`)
      setPhase('error')
    })()
    return () => {
      alive = false
    }
  }, [qrCode, slug, retryNonce, setSession, navigate])

  // ── Paso 3: unirse con el código ──────────────────────────────────────────
  async function submitJoinCode(e: React.FormEvent) {
    e.preventDefault()
    if (!qrCode) return
    const code = codeInput.trim()
    if (code.length < 4) {
      setCodeError('Escribe el código completo.')
      return
    }
    setCodeError(null)
    setPhase('joining')
    const body: { qrCode: string; joinCode: string; slug?: string } = {
      qrCode,
      joinCode: code,
    }
    if (slug) body.slug = slug
    const r = await joinSession(body)
    if (r.ok) {
      setSession(r.data)
      navigate(`/r/${slug}/menu`, { replace: true })
      return
    }
    // Código incorrecto / sesión cerrada → volver al formulario con el error.
    setPhase('need-code')
    setCodeError(
      r.error.code === 'JOIN_CODE_INVALID'
        ? r.error.message
        : `${r.error.code}: ${r.error.message}`,
    )
  }

  // ── Paso opcional: enviar el pre-registro ─────────────────────────────────
  // Captura nombre + teléfono + correo → alimenta el CRM (y suma puntos si el
  // negocio tiene lealtad activa). Nunca bloquea: si algo falla igual avanzamos
  // al código/menú (la identidad best-effort no debe atorar el pedido).
  async function submitRegister(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    const name = regName.trim()
    if (name.length < 2) {
      setRegError('Escribe tu nombre para continuar (o toca «omitir»).')
      return
    }
    const email = regEmail.trim()
    if (email && !email.includes('@')) {
      setRegError('Ese correo no se ve válido. Corrígelo o déjalo vacío.')
      return
    }
    const token = useSessionStore.getState().session?.token
    if (!token) {
      // Sin token no podemos identificar — no atorar al comensal.
      setPhase('opened')
      return
    }
    setRegBusy(true)
    setRegError(null)
    const body: { name: string; phone?: string; email?: string } = { name }
    if (regPhone.trim()) body.phone = regPhone.trim()
    if (email) body.email = email
    const r = await identifyGuest(token, body)
    setRegBusy(false)
    if (r.ok) {
      const customerName = r.data.customerName || name
      if (r.data.loyalty) {
        setLoyalty({ ...r.data.loyalty, customerName })
      } else {
        // Lealtad apagada: quedó registrado sin puntos → confirmamos en el menú.
        setLoyalty({ contactId: '', loyaltyPoints: 0, loyaltyVisits: 1, pointsEarned: 0, customerName })
      }
      setPhase('opened')
      return
    }
    // Falla del CRM: no bloquear el pedido — avisar suave y dejar avanzar.
    setRegError(`No pudimos guardar tus datos (${r.error.message}). Puedes continuar igual.`)
  }

  // ── Modo estadio: ubicación (y nombre opcional) antes del menú ───────────
  async function submitSeat(seat: SeatLocation): Promise<void> {
    if (!slug) return
    setSeat(slug, seat)
    const token = useSessionStore.getState().session?.token
    if (seat.name && token) {
      // Best-effort: el nombre lo ve quien entrega ("Este pedido es de").
      setRegBusy(true)
      await identifyGuest(token, { name: seat.name }).catch(() => undefined)
      setRegBusy(false)
    }
    navigate(`/r/${slug}/menu`, { replace: true })
  }

  if (stadium && (phase === 'opening' || (phase === 'register' && isTakeout))) {
    return (
      <div className="app st-app">
        <NintaiGOHeader slug={slug} tableCode={tableCode} branchId={sessionBranchId} />
        <div className="container">
          <section className="st-hero">
            <span className="st-hero__eyebrow">Pide desde tu asiento</span>
            <h1 className="st-hero__title">
              {branding?.name ?? 'Nintai'}{branding?.name ? null : <em>GO</em>}
            </h1>
            <p className="st-hero__sub">Compra, paga y recibe sin levantarte.</p>
          </section>
          {phase === 'opening' ? (
            <div className="st-card st-card--center">
              <span className="spinner" />
              <p className="st-muted">Preparando tu pedido…</p>
            </div>
          ) : (
            <div className="st-card">
              <h2 className="st-card__title">¿Dónde estás sentado?</h2>
              <p className="st-muted">Con tu sección, fila y asiento te llevamos el pedido hasta tu lugar.</p>
              <StadiumSeatForm
                initial={seatForSlug}
                busy={regBusy}
                submitLabel="Ver el menú"
                onSubmit={(v) => void submitSeat(v)}
              />
            </div>
          )}
        </div>
        <div className="footer">Powered by Nintai<em>GO</em></div>
      </div>
    )
  }

  return (
    <div className="app">
      <NintaiGOHeader slug={slug} tableCode={tableCode} branchId={sessionBranchId} />
      <div className="container">
        <div className="welcome">
          <img src="/favicon.svg" alt="" className="big-logo" />

          {/* ── Abriendo ─────────────────────────────────────────────── */}
          {phase === 'opening' && (
            <>
              <h1>
                Bienvenido a Nintai<em>GO</em>
              </h1>
              {/* Mensaje NEUTRO: en 'opening' aún no sabemos si es mesa o takeout
                  (lo resuelve checkInFromQr) → no decir "tu mesa" (rompe en takeout). */}
              <p>Un momento, estamos preparando todo…</p>
              <span className="spinner" />
            </>
          )}

          {/* ── Pre-registro (opcional) — antes del código/menú ─────────── */}
          {phase === 'register' && (
            <>
              <h1>
                Antes de <em>ordenar</em>
              </h1>
              <p>
                {loyaltyOn
                  ? `Déjanos tus datos para avisarte cuando tu ${isTakeout ? 'pedido' : 'orden'} esté listo${loyaltyPpv > 0 ? ` — y suma ${loyaltyPpv} pts de lealtad en cada visita.` : ' — y acumula puntos de lealtad.'}`
                  : `Déjanos tus datos para avisarte cuando tu ${isTakeout ? 'pedido' : 'orden'} esté listo y tenerte ubicado${isTakeout ? '' : ' en tu mesa'}.`}
              </p>
              <form onSubmit={submitRegister} style={{ width: '100%', maxWidth: 340 }}>
                <label className="label" htmlFor="regName">Nombre</label>
                <input
                  id="regName"
                  className="input"
                  value={regName}
                  onChange={(e) => { setRegName(e.target.value); if (regError) setRegError(null) }}
                  placeholder="Tu nombre"
                  autoComplete="given-name"
                  maxLength={80}
                  autoFocus
                />
                <div style={{ height: 10 }} />
                <label className="label" htmlFor="regPhone">Teléfono</label>
                <input
                  id="regPhone"
                  className="input"
                  value={regPhone}
                  onChange={(e) => setRegPhone(e.target.value)}
                  placeholder="55 1234 5678"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={32}
                />
                <div style={{ height: 10 }} />
                <label className="label" htmlFor="regEmail">Correo</label>
                <input
                  id="regEmail"
                  className="input"
                  value={regEmail}
                  onChange={(e) => { setRegEmail(e.target.value); if (regError) setRegError(null) }}
                  placeholder="tu@correo.com"
                  inputMode="email"
                  autoComplete="email"
                  maxLength={120}
                />
                {regError && <p className="code-error">{regError}</p>}
                <div style={{ height: 16 }} />
                <button type="submit" className="btn btn-primary" disabled={regBusy}>
                  {regBusy ? (<><span className="spinner" /> Guardando…</>) : 'Continuar'}
                </button>
              </form>
              {/* "omitir" discreto: texto plano, sin botón/pill — no compite con
                  el CTA "Continuar". */}
              <button
                type="button"
                onClick={() => setPhase('opened')}
                disabled={regBusy}
                style={{
                  marginTop: 16,
                  background: 'none',
                  border: 'none',
                  padding: 4,
                  color: 'var(--fg-muted)',
                  fontSize: 14,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: 3,
                }}
              >
                omitir
              </button>
            </>
          )}

          {/* ── Sesión abierta — pedido PARA LLEVAR ───────────────────── */}
          {phase === 'opened' && isTakeout && (
            <>
              <h1>
                Pedido <em>para llevar</em>
              </h1>
              {/* La sucursal, explícita: aquí es donde el comensal RECOGERÁ. */}
              {branchName && <span className="table-chip">Sucursal {branchName}</span>}
              <p>
                Arma tu pedido desde el menú. Al terminar, lo pagas y la cocina
                lo prepara — te avisamos cuando esté listo para recoger
                {branchName ? <> en <strong>{branchName}</strong></> : null}.
              </p>
              <div style={{ height: 14 }} />
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/r/${slug}/menu`, { replace: true })}
              >
                Ver el menú
              </button>
            </>
          )}

          {/* ── Sesión abierta: QR de mesa — código para compartir ─────── */}
          {phase === 'opened' && !isTakeout && (
            <>
              <h1>
                Tu mesa está <em>lista</em>
              </h1>
              {tableCode && <span className="table-chip">Mesa {tableCode}</span>}
              {openCode ? (
                <div className="card join-card">
                  <div className="join-eyebrow">Código de tu mesa</div>
                  <div className="join-code" aria-label="Código de la sesión">
                    {openCode}
                  </div>
                  <p className="join-hint">
                    Compártelo con quienes te acompañan: lo necesitan para unirse
                    a esta misma cuenta y ordenar contigo.
                  </p>
                </div>
              ) : (
                <p>Tu sesión está abierta.</p>
              )}
              <div style={{ height: 14 }} />
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/r/${slug}/menu`, { replace: true })}
              >
                Ir al menú
              </button>
            </>
          )}

          {/* ── Pedir el código de unión ──────────────────────────────── */}
          {(phase === 'need-code' || phase === 'joining') && (
            <>
              <h1>
                Únete a la <em>mesa</em>
              </h1>
              {tableCode && <span className="table-chip">Mesa {tableCode}</span>}
              <p>
                Esta mesa ya tiene una cuenta abierta. Ingresa el código que te
                compartió tu acompañante para unirte.
              </p>
              <form
                onSubmit={submitJoinCode}
                style={{ width: '100%', maxWidth: 320 }}
              >
                <label className="label" htmlFor="joinCode">
                  Código de la mesa
                </label>
                <input
                  id="joinCode"
                  className="input code-input"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={8}
                  placeholder="XXXXXX"
                  value={codeInput}
                  disabled={phase === 'joining'}
                  onChange={(e) => {
                    setCodeInput(e.target.value.toUpperCase())
                    if (codeError) setCodeError(null)
                  }}
                  autoFocus
                />
                {codeError && <p className="code-error">{codeError}</p>}
                <div style={{ height: 14 }} />
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={phase === 'joining' || codeInput.trim().length < 4}
                >
                  {phase === 'joining' ? (
                    <>
                      <span className="spinner" /> Uniéndote…
                    </>
                  ) : (
                    'Unirme a la mesa'
                  )}
                </button>
              </form>
            </>
          )}

          {/* ── Error ─────────────────────────────────────────────────── */}
          {phase === 'error' && (
            <>
              <h1>
                Nintai<em>GO</em>
              </h1>
              {!qrCode ? (
                <p>Escanea el QR de tu mesa para empezar a ordenar.</p>
              ) : (
                <>
                  <p className="danger">
                    {mesaNoDisponible ?? 'No pudimos abrir tu mesa.'}
                  </p>
                  {error && mesaNoDisponible === null && (
                    <div
                      className="card"
                      style={{ width: '100%', textAlign: 'left' }}
                    >
                      <div className="muted">Detalle técnico</div>
                      <code
                        style={{
                          fontSize: 12,
                          color: 'var(--danger)',
                          wordBreak: 'break-word',
                        }}
                      >
                        {error}
                      </code>
                    </div>
                  )}
                  <div style={{ height: 14 }} />
                  <button
                    className="btn btn-primary"
                    onClick={() => setRetryNonce((n) => n + 1)}
                  >
                    Reintentar
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
      <div className="footer">
        Powered by Nintai<em>GO</em> — {isTakeout ? 'ordena para llevar' : 'ordena desde tu mesa'}
      </div>
    </div>
  )
}
